/** Cashfree hosted checkout leaves the SPA (full-page POST). On return, browsers
 * often clear sessionStorage — our tab-session guard must not treat that as
 * "user closed tab" and sign them out before /payment-success renders.
 *
 * Some Cashfree / merchant redirects land on `/` first; stash the intended
 * return URL before leaving checkout and recover it synchronously on load.
 */

import { SITE_ORIGIN } from '../shared/seo/siteOrigin';

const PAYMENT_IN_PROGRESS_KEY = 'payment_in_progress';
const PAYMENT_RETURN_TARGET_KEY = 'payment_return_target';

const CASHFREE_RETURN_PARAM_KEYS = [
  'cf_checkoutStatus',
  'cf_authStatus',
  'authorization_status',
  'payment_status',
  'subscription_status',
  'cf_status',
  'txStatus',
  'tx_status',
  'order_status',
  'error_code',
  'error_message',
];

export function stashPaymentReturnTarget(params: {
  type?: 'recurring' | 'one_time';
  subscriptionId?: string;
  orderId?: string;
}): void {
  if (typeof window === 'undefined') return;
  const search = new URLSearchParams();
  if (params.type === 'recurring' && params.subscriptionId?.trim()) {
    search.set('type', 'recurring');
    search.set('subscription_id', params.subscriptionId.trim());
  } else if (params.orderId?.trim()) {
    search.set('type', 'purchase');
    search.set('order_id', params.orderId.trim());
  } else {
    return;
  }
  try {
    localStorage.setItem(PAYMENT_RETURN_TARGET_KEY, `/payment-success?${search.toString()}`);
  } catch {
    /* best-effort */
  }
}

export function readPaymentReturnTarget(): string | null {
  try {
    const raw = localStorage.getItem(PAYMENT_RETURN_TARGET_KEY);
    return raw?.startsWith('/payment-success') ? raw : null;
  } catch {
    return null;
  }
}

export function clearPaymentReturnTarget(): void {
  try {
    localStorage.removeItem(PAYMENT_RETURN_TARGET_KEY);
  } catch {
    /* best-effort */
  }
}

function mergePaymentReturnSearch(storedSearch: string, currentSearch: string): string {
  const stored = new URLSearchParams(storedSearch.startsWith('?') ? storedSearch.slice(1) : storedSearch);
  const current = new URLSearchParams(currentSearch.startsWith('?') ? currentSearch.slice(1) : currentSearch);
  for (const key of CASHFREE_RETURN_PARAM_KEYS) {
    const v = current.get(key);
    if (v != null && String(v).trim()) stored.set(key, String(v).trim());
  }
  const qs = stored.toString();
  return qs ? `?${qs}` : '';
}

function paymentReturnTargetNeedsRedirect(pathname: string, search: string): boolean {
  const target = readPaymentReturnTarget();
  if (!target) return false;
  const [targetPath, targetSearch = ''] = target.split('?');
  if (pathname !== targetPath) return true;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const targetParams = new URLSearchParams(targetSearch);
  if (targetParams.has('subscription_id') && !params.has('subscription_id')) return true;
  if (targetParams.has('order_id') && !params.has('order_id')) return true;
  return false;
}

/** Cashfree may land on `/` while payment_return_target points at /payment-success. */
export function isRecoveringPaymentOnHome(pathname?: string): boolean {
  if (typeof window === 'undefined') return false;
  const p = (pathname ?? window.location.pathname).replace(/\/+$/, '') || '/';
  if (p !== '/') return false;
  try {
    return isPaymentFlowInProgress() && !!readPaymentReturnTarget();
  } catch {
    return false;
  }
}

export function isPaymentBootRoute(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/payment-success' || p === '/payment-failed' || p === '/checkout') return true;
  return isRecoveringPaymentOnHome(p);
}

export function isPaymentReturnPath(pathname: string, search = ''): boolean {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/payment-failed') return false;
  if (p === '/payment-success') {
    return true;
  }
  // Wrong path but Cashfree return is in flight — treat as payment return until recovered.
  try {
    return isPaymentFlowInProgress() && !!readPaymentReturnTarget();
  } catch {
    return false;
  }
}

/** Remove prerender flash guard once React is about to mount. */
export function finalizePaymentBoot(): void {
  if (typeof document === 'undefined') return;
  document.getElementById('ggj-pay-boot')?.remove();
  document.documentElement.classList.remove('ggj-pay-boot');
  const root = document.getElementById('root');
  if (root && isPaymentBootRoute(window.location.pathname)) {
    root.style.visibility = '';
  }
}

/** Call synchronously before React mount on payment-return URLs. */
export function bootstrapPaymentReturn(): void {
  if (typeof window === 'undefined') return;

  const { pathname, search, hostname } = window.location;

  // Apex → www before any SPA logic (GET returns; POST is handled by edge bridge).
  if (hostname.toLowerCase() === 'gograbjob.com') {
    window.location.replace(`${SITE_ORIGIN}${pathname}${search}`);
    return;
  }

  if (isPaymentBootRoute(pathname)) {
    finalizePaymentBoot();
  }

  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/payment-failed') {
    clearPaymentFlowFlags();
    try {
      sessionStorage.setItem('tab_session_active', Date.now().toString());
    } catch {
      /* best-effort */
    }
    return;
  }

  const { origin } = window.location;
  const inProgress = isPaymentFlowInProgress();
  const target = readPaymentReturnTarget();

  try {
    sessionStorage.setItem('tab_session_active', Date.now().toString());
    if (inProgress || target) {
      localStorage.setItem(PAYMENT_IN_PROGRESS_KEY, 'true');
    }
  } catch {
    /* best-effort */
  }

  if (inProgress && target && paymentReturnTargetNeedsRedirect(pathname, search)) {
    // Never yank the user off the terminal failure page back to verification.
    if (p === '/payment-failed') return;

    const [, targetSearch = ''] = target.split('?');
    const merged = mergePaymentReturnSearch(
      targetSearch ? `?${targetSearch}` : '',
      search,
    );
    const nextPath = `/payment-success${merged}`;
    // On `/`, replaceState avoids a second full reload (homepage flash) after index.html recovery.
    if (pathname === '/' || pathname === '') {
      window.history.replaceState({}, '', nextPath);
    } else {
      window.location.replace(`${origin}${nextPath}`);
    }
  }
}

export function isPaymentFlowInProgress(): boolean {
  try {
    return localStorage.getItem(PAYMENT_IN_PROGRESS_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Clear all payment-return markers after the user leaves the result page. */
export function clearPaymentFlowFlags(): void {
  try {
    localStorage.removeItem(PAYMENT_IN_PROGRESS_KEY);
    clearPaymentReturnTarget();
    sessionStorage.removeItem('pending_payment');
    sessionStorage.setItem('tab_session_active', Date.now().toString());
  } catch {
    /* best-effort */
  }
}

// ── Session recovery for mobile UPI/bank redirects ──────────────────────────
// Mobile browsers (especially in-app WebViews opened by UPI apps like Google
// Pay, PhonePe, Paytm) can wipe localStorage when switching back from the
// payment app. We stash the Supabase refresh_token in a short-lived cookie
// (survives cross-app navigations) so the payment result page can restore
// the session even when localStorage is cleared.
const SESSION_RECOVERY_COOKIE = 'ggj_pay_rt';
const SESSION_RECOVERY_LS_KEY = 'ggj_pay_session_recovery';

/** Stash the refresh token before redirecting to Cashfree. */
export function stashSessionForPaymentRedirect(refreshToken: string): void {
  if (!refreshToken) return;
  try {
    // Cookie: SameSite=Lax survives top-level navigations; 30 min max-age
    const maxAge = 1800;
    document.cookie = `${SESSION_RECOVERY_COOKIE}=${encodeURIComponent(refreshToken)};path=/;max-age=${maxAge};SameSite=Lax;Secure`;
    // Redundant localStorage key (separate from Supabase's own key)
    localStorage.setItem(SESSION_RECOVERY_LS_KEY, refreshToken);
  } catch {
    /* best-effort */
  }
}

/** Read the stashed refresh token (cookie first, then fallback localStorage). */
export function readSessionRecoveryToken(): string | null {
  // 1. Cookie
  try {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const [name, ...rest] = c.trim().split('=');
      if (name === SESSION_RECOVERY_COOKIE) {
        const val = decodeURIComponent(rest.join('='));
        if (val) return val;
      }
    }
  } catch { /* */ }
  // 2. Fallback localStorage (may survive when cookie doesn't)
  try {
    const val = localStorage.getItem(SESSION_RECOVERY_LS_KEY);
    if (val) return val;
  } catch { /* */ }
  return null;
}

/** Clear all session recovery stashes (call on successful session restore or sign-out). */
export function clearSessionRecoveryToken(): void {
  try {
    document.cookie = `${SESSION_RECOVERY_COOKIE}=;path=/;max-age=0;SameSite=Lax;Secure`;
  } catch { /* */ }
  try {
    localStorage.removeItem(SESSION_RECOVERY_LS_KEY);
  } catch { /* */ }
}
